import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { ContentBody } from '../../../../../../atoms/ContentBody/ContentBody';
import { H1 } from '../../../../../../atoms/Heading/Heading';
import { Button } from '../../../../../../atoms/Button/Button';
import { Footer } from '../../../../../../atoms/Footer/Footer';
import { ScreenWrapper } from '../../../../../../atoms/ScreenWrapper/ScreenWrapper';
import { BankIdLogo } from '../../../../../../atoms/BankIdLogo/BankIdLogo';
import { LegalDisclaimer } from '../../../../../../molecules/LegalDisclaimer/LegalDisclaimer';
import {
  AwaitingScreenHeader,
  AwaitingScreenHeaderProps,
} from '../../../molecules/AwaitingScreenHeader/AwaitingScreenHeader';
import { PageView } from '../../../../../../../models/analytics';
import { ProviderEntity } from '../../../../../../../models/provider';
import { Stack } from '../../../../../../atoms/Stack/Stack';
import { usePageViewEvents } from '../../../../../../../core/Analytics/usePageViewEvents';
import { messages } from './messages';
import { StyledBankId } from './AwaitingMobileBankIdAuthenticationTarget.styles';

export type AwaitingMobileBankIdAuthenticationTargetProps = AwaitingScreenHeaderProps & {
  provider: ProviderEntity;
  onLocalDeviceClick: () => void;
  onRemoteDeviceClick: () => void;
  footerDisclaimer: React.ReactNode | undefined;
  heading: React.ReactNode;
};

export const AwaitingMobileBankIdAuthenticationTarget: React.FC<
  AwaitingMobileBankIdAuthenticationTargetProps
> = ({
  provider,
  onLocalDeviceClick,
  onRemoteDeviceClick,
  heading,
  footerDisclaimer,
  pageView = PageView.BANKID_TARGET_AWAITING_SCREEN,
  ...rest
}) => {
  usePageViewEvents({ pageView });

  return (
    <ScreenWrapper data-testid="awaiting-mobile-bankid-target-screen">
      <AwaitingScreenHeader {...rest} pageView={pageView} />

      <ContentBody>
        <Stack justifyContent="center">
          <BankIdLogo size="110px" px="normedium" py="normedium" mx="auto" my="medium" />
          <H1 mb="small" align="center">
            {heading}
          </H1>
        </Stack>
        <Footer stickToBottom as="div">
          {footerDisclaimer && (
            <Box mb="normal" fullWidth>
              {footerDisclaimer}
            </Box>
          )}
          <Button
            color="primary"
            onClick={onLocalDeviceClick}
            variant="contained"
            data-testid="local-device-action"
          >
            <StyledBankId />
            <FormattedMessage {...messages.openMobileBankId} />
          </Button>
          <Button color="primary" onClick={onRemoteDeviceClick} data-testid="remote-device-action">
            <FormattedMessage {...messages.anotherDevice} />
          </Button>
        </Footer>
      </ContentBody>
      <LegalDisclaimer provider={provider} pageView={pageView} />
    </ScreenWrapper>
  );
};
